import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { toFormData } from '@platform/shared/utilities/file-utility';
import { environment } from '@environments/environment';

import { PortalTaskDocument } from '@apps/fiduciary/fiduciary.state';
import {
  PortalTaskCommentDto,
  PortalTaskDto,
  PortalTaskStatusDto,
  PostPortalTaskCommentDto,
  PostPortalTaskDto,
  SharepointTaskCompletedDto,
  TaskOverviewDto,
} from './tasks.model';

@Injectable({
  providedIn: 'root',
})
export class TasksService {
  constructor(private http: HttpClient) {}

  public createPortalTask(
    customerSpaceId: string,
    task: PostPortalTaskDto,
    files: FileList | File[] | undefined,
  ): Observable<PortalTaskDto> {
    let formData = new FormData();
    formData.append('dto', JSON.stringify(task));
    if (files && files.length) {
      formData = toFormData(files);
      formData.append('dto', JSON.stringify(task));
    }
    return this.http.post<PortalTaskDto>(`${environment.apiUrl}/customer-spaces/${customerSpaceId}/fiduciary/portal-tasks`, formData);
  }

  public updatePortalTask(customerSpaceId: string, taskId: string, task: PostPortalTaskDto): Observable<PortalTaskDto> {
    return this.http.put<PortalTaskDto>(`${environment.apiUrl}/customer-spaces/${customerSpaceId}/fiduciary/portal-tasks/${taskId}`, task);
  }

  public deletePortalTask(customerSpaceId: string, taskId: string): Observable<object> {
    return this.http.delete(`${environment.apiUrl}/customer-spaces/${customerSpaceId}/fiduciary/portal-tasks/${taskId}`);
  }

  public getPortalTask(customerSpaceId: string, taskId: string): Observable<PortalTaskDto> {
    return this.http.get<PortalTaskDto>(`${environment.apiUrl}/customer-spaces/${customerSpaceId}/fiduciary/portal-tasks/${taskId}`);
  }

  public createPortalTaskComment(
    customerSpaceId: string,
    taskId: string,
    comment: PostPortalTaskCommentDto,
  ): Observable<PortalTaskCommentDto[]> {
    return this.http.post<PortalTaskCommentDto[]>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/fiduciary/portal-tasks/${taskId}/comments`,
      comment,
    );
  }

  public deletePortalTaskComment(customerSpaceId: string, taskId: string, commentId: string): Observable<PortalTaskCommentDto[]> {
    return this.http.delete<PortalTaskCommentDto[]>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/fiduciary/portal-tasks/${taskId}/comments/${commentId}`,
    );
  }

  public getPortalTasks(
    customerSpaceId: string,
    getAssignedTo: boolean,
    getCreatedBy: boolean,
    statuses?: PortalTaskStatusDto[],
    completed?: SharepointTaskCompletedDto,
    top?: number,
  ): Observable<TaskOverviewDto[]> {
    let params = new HttpParams();
    if (getAssignedTo === true) {
      params = params.set('getAssignedTo', 'true');
    }

    if (getCreatedBy === true) {
      params = params.set('getCreatedBy', 'true');
    }

    if (top) {
      params = params.set('top', top.toString());
    }

    if (statuses) {
      for (const status of statuses) {
        params = params.append('statuses', status);
      }
    }

    if (completed) {
      params = params.append('completed', completed);
    }

    const options = params.keys().length > 0 ? { params } : {};
    return this.http.get<TaskOverviewDto[]>(`${environment.apiUrl}/customer-spaces/${customerSpaceId}/fiduciary/portal-tasks`, options);
  }

  public getAllCustomerSpacesPortalTasks(statuses?: PortalTaskStatusDto[], top?: number): Observable<TaskOverviewDto[]> {
    let params = new HttpParams();

    if (top) {
      params = params.set('top', top.toString());
    }

    if (statuses) {
      for (const status of statuses) {
        params = params.append('statuses', status);
      }
    }

    const options = params.keys().length > 0 ? { params } : {};
    return this.http.get<TaskOverviewDto[]>(`${environment.apiUrl}/tasks`, options);
  }

  public uploadDocument(customerSpaceId: string, taskId: string, files: FileList) {
    const formData = toFormData(files);

    return this.http.post<PortalTaskDocument[]>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/fiduciary/portal-tasks/${taskId}/documents`,
      formData,
    );
  }

  public downloadDocument(customerSpaceId: string, taskId: string, documentId: string): Observable<Blob> {
    return this.http.get(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/fiduciary/portal-tasks/${taskId}/documents/${documentId}`,
      { responseType: 'blob' },
    );
  }

  public deletePortalTaskDocument(customerSpaceId: string, taskId: string, documentId: string): Observable<PortalTaskDocument[]> {
    return this.http.delete<PortalTaskDocument[]>(
      `${environment.apiUrl}/customer-spaces/${customerSpaceId}/fiduciary/portal-tasks/${taskId}/documents/${documentId}`,
    );
  }
}
